import { DEFAULT_CHARACTER_ID } from "@shared/characterCatalog.js";
import {
  REMOTE_INTERP_BASE_DELAY_MS,
  REMOTE_INTERP_MAX_DELAY_MS,
  REMOTE_INTERP_MIN_DELAY_MS,
  REMOTE_JITTER_FACTOR,
  REMOTE_JITTER_WINDOW,
  REMOTE_MAX_EXTRAPOLATION_MS,
  REMOTE_SNAP_DISTANCE_PX,
} from "../../../net/networkTuning.js";

const REMOTE_BASE_LERP = 0.14;
const REMOTE_MID_LERP = 0.22;
const REMOTE_HIGH_LERP = 0.34;
const REMOTE_MID_DISTANCE_PX = 24;
const REMOTE_HIGH_DISTANCE_PX = 72;
const DEFAULT_SERVER_TICK_RATE = 30;
const REMOTE_SNAPSHOT_PRUNE_MS = 1500;
const REMOTE_MAX_SNAPSHOTS = 64;
const REMOTE_SNAP_COOLDOWN_MS = 160;
const REMOTE_TELEPORT_SNAP_DISTANCE_PX = 640;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function computeAdaptiveDelay(remote, scene) {
  const safeTickRate = Number.isFinite(Number(scene?.netServerTickRate))
    ? Math.max(1, Number(scene.netServerTickRate))
    : DEFAULT_SERVER_TICK_RATE;
  const stateIntervalMs = 1000 / safeTickRate;
  const baseFromTick = Math.max(REMOTE_INTERP_MIN_DELAY_MS, stateIntervalMs * 2);
  const baseDelayMs = Number.isFinite(REMOTE_INTERP_BASE_DELAY_MS)
    ? Math.max(baseFromTick, Number(REMOTE_INTERP_BASE_DELAY_MS))
    : baseFromTick;

  const intervals = Array.isArray(remote?.arrivalIntervalsMs) ? remote.arrivalIntervalsMs : [];
  if (!intervals.length) {
    const delayMs = clamp(baseDelayMs, REMOTE_INTERP_MIN_DELAY_MS, REMOTE_INTERP_MAX_DELAY_MS);
    return {
      delayMs,
      stateIntervalMs,
      meanArrivalMs: stateIntervalMs,
      jitterMs: 0,
    };
  }

  const sum = intervals.reduce((acc, value) => acc + value, 0);
  const mean = sum / intervals.length;
  const variance = intervals.reduce((acc, value) => {
    const d = value - mean;
    return acc + d * d;
  }, 0) / intervals.length;
  const stddev = Math.sqrt(Math.max(0, variance));
  const delayMs = clamp(
    baseDelayMs + REMOTE_JITTER_FACTOR * stddev,
    REMOTE_INTERP_MIN_DELAY_MS,
    REMOTE_INTERP_MAX_DELAY_MS,
  );
  return {
    delayMs,
    stateIntervalMs,
    meanArrivalMs: mean,
    jitterMs: stddev,
  };
}

export function createRemotePlayer(scene, player, options = {}) {
  const feetUiOffsetY = Number.isFinite(options.feetUiOffsetY) ? options.feetUiOffsetY : 6;
  const characterId = scene.sanitizeCharacterId(player.characterId);
  const characterCenterOffsetX = scene.getCharacterCenterOffsetX(characterId);
  const x = (Number.isFinite(player.x) ? player.x : scene.spawnX) + 14 - characterCenterOffsetX;
  const characterFootOffset = scene.getCharacterFootOffset(characterId);
  const y = (Number.isFinite(player.y) ? player.y : scene.spawnY) + 40 + characterFootOffset;
  const sprite = scene.add.sprite(x, y, scene.resolvePlayableAtlasForState(characterId, "Idle"))
    .setDepth(1000 + y);
  scene.applySpriteOriginForCharacter(sprite, characterId);
  const idleKey = scene.resolvePlayableAnimKey(characterId, "Idle");
  if (idleKey) sprite.play(idleKey);
  const label = scene.add.text(x, y - characterFootOffset + feetUiOffsetY, player.name || "Player", {
    fontFamily: "monospace",
    fontSize: "11px",
    color: "rgba(255,255,255,0.9)",
    stroke: "#000000",
    strokeThickness: 3,
  }).setOrigin(0.5, 0).setDepth(1001 + y).setPadding(4, 1, 4, 1).setBackgroundColor("rgba(0,0,0,0.35)");

  const initialSnapshot = {
    receivedAt: Date.now(),
    x: Number.isFinite(player.x) ? player.x : scene.spawnX,
    y: Number.isFinite(player.y) ? player.y : scene.spawnY,
    vx: Number.isFinite(player.vx) ? player.vx : 0,
    vy: Number.isFinite(player.vy) ? player.vy : 0,
    facing: player.facing >= 0 ? 1 : -1,
    anim: typeof player.anim === "string" ? player.anim : "",
  };

  return {
    id: player.id,
    name: player.name || "Player",
    characterId,
    sprite,
    label,
    targetX: Number.isFinite(player.x) ? player.x : scene.spawnX,
    targetY: Number.isFinite(player.y) ? player.y : scene.spawnY,
    vx: 0,
    vy: 0,
    facing: player.facing >= 0 ? 1 : -1,
    anim: typeof player.anim === "string" ? player.anim : "",
    hurtUntil: 0,
    knockbackVx: 0,
    lastSeenAt: scene.time.now,
    stateReceivedAt: Date.now(),
    snapshots: [initialSnapshot],
    arrivalIntervalsMs: [],
    lastArrivedAt: Number.isFinite(initialSnapshot.receivedAt) ? initialSnapshot.receivedAt : Date.now(),
    adaptiveDelayMs: REMOTE_INTERP_MIN_DELAY_MS,
    smoothedLerp: REMOTE_BASE_LERP,
    lastSnapAt: 0,
  };
}

export function pushRemoteSnapshot(remote, snapshot) {
  if (!remote || !snapshot) return;
  if (!Array.isArray(remote.snapshots)) remote.snapshots = [];
  const last = remote.snapshots[remote.snapshots.length - 1];
  if (last && Number.isFinite(last.receivedAt) && Number.isFinite(snapshot.receivedAt)) {
    if (snapshot.receivedAt <= last.receivedAt) {
      // Ignore out-of-order/duplicate packets for interpolation timeline stability.
      return;
    }
  }
  remote.snapshots.push(snapshot);
  if (remote.snapshots.length > REMOTE_MAX_SNAPSHOTS) {
    remote.snapshots.splice(0, remote.snapshots.length - REMOTE_MAX_SNAPSHOTS);
  }

  const arrivedAt = Number.isFinite(snapshot.arrivedAt) ? Number(snapshot.arrivedAt) : Number(snapshot.receivedAt);
  if (!Number.isFinite(arrivedAt)) return;
  const lastArrivedAt = Number.isFinite(remote.lastArrivedAt) ? Number(remote.lastArrivedAt) : null;
  remote.lastArrivedAt = arrivedAt;
  if (!Number.isFinite(lastArrivedAt) || arrivedAt <= lastArrivedAt) return;
  const intervalMs = arrivedAt - lastArrivedAt;
  if (!Number.isFinite(intervalMs) || intervalMs <= 0 || intervalMs > 1000) return;
  if (!Array.isArray(remote.arrivalIntervalsMs)) remote.arrivalIntervalsMs = [];
  remote.arrivalIntervalsMs.push(intervalMs);
  if (remote.arrivalIntervalsMs.length > REMOTE_JITTER_WINDOW) {
    remote.arrivalIntervalsMs.splice(0, remote.arrivalIntervalsMs.length - REMOTE_JITTER_WINDOW);
  }
}

function resolveInterpolatedState(remote, now, interpolationDelayMs) {
  const snapshots = Array.isArray(remote.snapshots) ? remote.snapshots : [];
  if (!snapshots.length) return null;

  const minKeepAt = now - REMOTE_SNAPSHOT_PRUNE_MS;
  while (snapshots.length > 2 && snapshots[1].receivedAt < minKeepAt) {
    snapshots.shift();
  }

  const renderAt = now - interpolationDelayMs;
  const first = snapshots[0];
  const last = snapshots[snapshots.length - 1];
  if (renderAt <= first.receivedAt) return first;
  if (renderAt >= last.receivedAt) {
    const msAhead = Math.max(0, Math.min(REMOTE_MAX_EXTRAPOLATION_MS, renderAt - last.receivedAt));
    const framesAhead = msAhead / (1000 / 60);
    return {
      ...last,
      x: last.x + (Number.isFinite(last.vx) ? last.vx : 0) * framesAhead,
      y: last.y + (Number.isFinite(last.vy) ? last.vy : 0) * framesAhead,
      isExtrapolated: true,
    };
  }

  for (let i = 1; i < snapshots.length; i += 1) {
    const prev = snapshots[i - 1];
    const next = snapshots[i];
    if (renderAt < prev.receivedAt || renderAt > next.receivedAt) continue;
    const span = Math.max(1, next.receivedAt - prev.receivedAt);
    const t = Math.max(0, Math.min(1, (renderAt - prev.receivedAt) / span));
    return {
      receivedAt: renderAt,
      x: prev.x + (next.x - prev.x) * t,
      y: prev.y + (next.y - prev.y) * t,
      vx: prev.vx + (next.vx - prev.vx) * t,
      vy: prev.vy + (next.vy - prev.vy) * t,
      facing: t < 0.5 ? prev.facing : next.facing,
      anim: t < 0.5 ? prev.anim : next.anim,
      isExtrapolated: false,
    };
  }
  return { ...last, isExtrapolated: false };
}

export function removeRemotePlayer(scene, playerId) {
  const remote = scene.remotePlayers.get(playerId);
  if (!remote) return;
  remote.sprite?.destroy();
  remote.label?.destroy();
  scene.remotePlayers.delete(playerId);
}

export function updateRemotePlayers(scene, dtScale = 1) {
  const scale = dtScale || 1;
  const now = Date.now();
  const sceneNow = scene.time.now;
  const feetUiOffsetY = Number.isFinite(scene.remotePlayerLabelOffsetY) ? scene.remotePlayerLabelOffsetY : 6;
  let remoteCount = 0;
  let delayTotalMs = 0;
  let delayMinMs = Infinity;
  let delayMaxMs = 0;
  let jitterTotalMs = 0;
  let arrivalTotalMs = 0;
  let extrapolatedCount = 0;
  let snapCount = 0;
  let forcedSnapCount = 0;
  if (!Array.isArray(scene.netRemoteSnapEventsMs)) scene.netRemoteSnapEventsMs = [];
  for (const remote of scene.remotePlayers.values()) {
    const sprite = remote.sprite;
    if (!sprite) continue;
    const delayMetrics = computeAdaptiveDelay(remote, scene);
    remote.adaptiveDelayMs = delayMetrics.delayMs;
    const sampled = resolveInterpolatedState(remote, now, delayMetrics.delayMs);
    const sampledX = Number.isFinite(sampled?.x) ? sampled.x : remote.targetX;
    const sampledY = Number.isFinite(sampled?.y) ? sampled.y : remote.targetY;
    const sampledVx = Number.isFinite(sampled?.vx) ? sampled.vx : remote.vx;
    const sampledVy = Number.isFinite(sampled?.vy) ? sampled.vy : remote.vy;
    const sampledFacing = Number.isFinite(sampled?.facing) ? sampled.facing : remote.facing;
    const sampledAnim = typeof sampled?.anim === "string" ? sampled.anim : remote.anim;
    const remoteCenterOffsetX = scene.getCharacterCenterOffsetX(remote.characterId || DEFAULT_CHARACTER_ID);
    const targetSpriteX = sampledX + 14 - remoteCenterOffsetX;
    const remoteFootOffset = scene.getCharacterFootOffset(remote.characterId || DEFAULT_CHARACTER_ID);
    const targetSpriteY = sampledY + 40 + remoteFootOffset;
    const distanceToTarget = Math.hypot(targetSpriteX - sprite.x, targetSpriteY - sprite.y);
    const extrapolated = !!sampled?.isExtrapolated;
    if (extrapolated) extrapolatedCount += 1;
    const baseLerp = extrapolated
      ? (distanceToTarget >= REMOTE_HIGH_DISTANCE_PX
        ? REMOTE_HIGH_LERP
        : (distanceToTarget >= REMOTE_MID_DISTANCE_PX ? REMOTE_MID_LERP : REMOTE_BASE_LERP))
      : 1;
    if (!Number.isFinite(remote.smoothedLerp)) remote.smoothedLerp = baseLerp;
    remote.smoothedLerp += (baseLerp - remote.smoothedLerp) * 0.22;
    const effectiveBaseLerp = extrapolated ? clamp(remote.smoothedLerp, REMOTE_BASE_LERP, 0.95) : 1;
    const lerp = extrapolated ? (1 - Math.pow(1 - effectiveBaseLerp, scale)) : 1;
    const sampledSpeed = Math.hypot(sampledVx, sampledVy);
    const velocityFactor = clamp(sampledSpeed / 6, 0, 1.6);
    const snapDistancePx = REMOTE_SNAP_DISTANCE_PX * (1.05 + velocityFactor * 0.85);
    if (Math.abs(remote.knockbackVx) > 0.01) {
      sprite.x += remote.knockbackVx * scale;
      remote.knockbackVx *= Math.pow(0.82, scale);
    }
    const lastSnapAt = Number.isFinite(remote.lastSnapAt) ? remote.lastSnapAt : 0;
    const canSnapNow = (now - lastSnapAt) >= REMOTE_SNAP_COOLDOWN_MS;
    const shouldForceSnap = distanceToTarget >= REMOTE_TELEPORT_SNAP_DISTANCE_PX;
    if (shouldForceSnap || (distanceToTarget > snapDistancePx && canSnapNow)) {
      sprite.x = targetSpriteX;
      sprite.y = targetSpriteY;
      remote.lastSnapAt = now;
      snapCount += 1;
      if (shouldForceSnap) forcedSnapCount += 1;
      scene.netRemoteSnapEventsMs.push(now);
    } else if (lerp >= 1) {
      sprite.x = targetSpriteX;
      sprite.y = targetSpriteY;
    } else {
      sprite.x += (targetSpriteX - sprite.x) * lerp;
      sprite.y += (targetSpriteY - sprite.y) * lerp;
    }
    sprite.setDepth(1000 + sprite.y);
    sprite.setFlipX(sampledFacing === -1);
    sprite.clearTint();

    const remoteCharacterId = remote.characterId || DEFAULT_CHARACTER_ID;
    const replicatedAnim = typeof sampledAnim === "string"
      ? scene.resolvePlayableAnimKey(remoteCharacterId, sampledAnim, "Idle")
      : null;
    const speed = Math.abs(sampledVx);
    const fallbackAnim = scene.resolvePlayableAnimKey(
      remoteCharacterId,
      speed > 1.5 ? "Running" : speed > 0.2 ? "Walking" : "Idle"
    );
    const dyingAnim = scene.resolvePlayableAnimKey(remoteCharacterId, "Dying", "Idle");
    const hurtAnim = scene.resolvePlayableAnimKey(remoteCharacterId, "Hurt", "Idle");
    const isDyingAnim = !!(replicatedAnim && dyingAnim && replicatedAnim === dyingAnim);
    const hurtActive = Number.isFinite(remote.hurtUntil) && remote.hurtUntil > sceneNow;
    const nextAnim = isDyingAnim
      ? replicatedAnim
      : (hurtActive ? hurtAnim : (replicatedAnim || fallbackAnim));
    if (nextAnim && sprite.anims.currentAnim?.key !== nextAnim) {
      sprite.play(nextAnim);
    }

    if (remote.label) {
      const remoteFeetY = sprite.y - remoteFootOffset;
      remote.label.x = sprite.x;
      remote.label.y = remoteFeetY + feetUiOffsetY;
      remote.label.setDepth(1001 + sprite.y);
    }

    remoteCount += 1;
    delayTotalMs += delayMetrics.delayMs;
    delayMinMs = Math.min(delayMinMs, delayMetrics.delayMs);
    delayMaxMs = Math.max(delayMaxMs, delayMetrics.delayMs);
    jitterTotalMs += delayMetrics.jitterMs;
    arrivalTotalMs += delayMetrics.meanArrivalMs;
  }
  const minKeepSnapAt = now - 60000;
  while (scene.netRemoteSnapEventsMs.length && scene.netRemoteSnapEventsMs[0] < minKeepSnapAt) {
    scene.netRemoteSnapEventsMs.shift();
  }
  if (!remoteCount) {
    return {
      remoteCount: 0,
      avgDelayMs: 0,
      minDelayMs: 0,
      maxDelayMs: 0,
      avgJitterMs: 0,
      avgArrivalMs: 0,
      extrapolationRatio: 0,
      snapCountLastMinute: scene.netRemoteSnapEventsMs.length,
      snapCountFrame: 0,
      forcedSnapCountFrame: 0,
    };
  }
  return {
    remoteCount,
    avgDelayMs: delayTotalMs / remoteCount,
    minDelayMs: Number.isFinite(delayMinMs) ? delayMinMs : 0,
    maxDelayMs: delayMaxMs,
    avgJitterMs: jitterTotalMs / remoteCount,
    avgArrivalMs: arrivalTotalMs / remoteCount,
    extrapolationRatio: extrapolatedCount / remoteCount,
    snapCountLastMinute: scene.netRemoteSnapEventsMs.length,
    snapCountFrame: snapCount,
    forcedSnapCountFrame: forcedSnapCount,
  };
}
